import { Pagination, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import Tooltip from '@mui/material/Tooltip';
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { iclApi, subjectsApi } from '../../../api';
import copySvg from '../../../assets/img/copy.svg';
import { getStudents, getTeachers } from '../../../Common/Database';
import { checklistTablePopupsNames } from '../../../constants/checklistTablePopupsNames';
import { tabs } from '../../../constants/checlistTableTabs';
import { tableSize } from '../../../constants/tableSize';
import { ICL, Option } from '../../../shared/types';
import { PersonOption } from '../../../shared/types/works';
import { ItemActionModal } from '../../../shared/ui/ItemActionModal';
import { LoadingError } from '../../LoadingError';
import CheckListFilters from '../CheckListFilters';
import TableTabs from '../TableTabs';

const adminTabs = [
  { tabName: 'library', tabKey: tabs.all },
  { tabName: 'For Consideration', tabKey: tabs.consideration },
  { tabName: 'Templates', tabKey: tabs.template },
  { tabName: 'Deleted Templates', tabKey: tabs.deletedTemplates },
];

export default function ChecklistTableAdmin() {
  const {
    t,
    i18n: { language: platformLang },
  } = useTranslation();

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const tabQuery = searchParams.get('tab');

  const [icls, setIcls] = useState<ICL[]>([]);
  const [iclsForShow, setIclsForShow] = useState<ICL[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isRemovingLoading, setIsRemovingLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState<string>('');
  const [idForManipulation, setIdForManipulation] = useState<string>('');
  const [tab, setTab] = useState<string>(tabs.all);
  const [page, setPage] = useState(1);
  const [students, setStudents] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [subjects, setSubjects] = useState<Option[] | null>(null);
  const [click, setClick] = useState(false);

  const [subjectFilter, setSubjectFilter] = useState<Option | null>(null);
  const [studentFilter, setStudentFilter] = useState<PersonOption | null>(null);
  const [teacherFilter, setTeacherFilter] = useState<PersonOption | null>(null);
  const [classFilter, setClassFilter] = useState<string | null>(null);

  useEffect(() => {
    setTab(tabQuery || tabs.all);
  }, [tabQuery]);

  useEffect(() => {
    const updateSubjects = (subjects: Option[]) => setSubjects(subjects);
    const unsubscribe = subjectsApi.subscribeToChanges(updateSubjects);

    return () => {
      unsubscribe();
    };
  }, []);

  const fetchTeachersListData = async () => {
    const teacherList = await getTeachers();
    if (teacherList) {
      const mapList = Object.values(teacherList);
      setTeachers(mapList);
    }
  };

  useEffect(() => {
    if (iclsForShow.length === 0 && page > 1) {
      setPage(page - 1);
    }
  }, [iclsForShow]);

  useEffect(() => {
    const init = async () => {
      setIsLoading(true);

      try {
        const res = getStudents();
        res.then(resp => {
          const arrStudents = Object.values(resp);
          setStudents(arrStudents);
        });
      } catch (error) {
        console.log(error);
      }

      setIsLoading(false);
    };

    init();

    fetchTeachersListData();
  }, []);

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.consideration) {
      const unsubscribe = iclApi.subscribeToAdminConsiderationICLs({
        setNewICLs: setIcls,
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.template) {
      const unsubscribe = iclApi.subscribeToTemplateICLs({
        setNewICLs: setIcls,
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.all) {
      const unsubscribe = iclApi.subscribeToAdminICLs({
        setNewICLs: setIcls,
        classFilter,
        subjectFilter: subjectFilter?.id,
        teacherFilter,
        studentFilter,
      });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [click, tab]);

  const activeIcls = useMemo(() => {
    return iclsForShow.filter(icl => !icl.deleted);
  }, [iclsForShow, tab, icls]);

  const removedIcls = useMemo(() => {
    return iclsForShow.filter(icl => icl.deleted);
  }, [iclsForShow, tab, icls]);

  const restoreDeletedIcl = async (iclId: string) => {
    try {
      await iclApi.restoreICL(iclId);
      toast.success(t('Successfully restored!'));
    } catch (error) {
      toast.error(t('Something went wrong. Try again!'));
    }
  };

  useEffect(() => {
    setIsLoading(true);

    if (tab === tabs.consideration) {
      const unsubscribe = iclApi.subscribeToAdminConsiderationICLs({
        setNewICLs: newIcls => {
          setIcls(newIcls);
          setIclsForShow(updateIclsForShow(newIcls, page));
        },
        classFilter,
        subjectFilter: subjectFilter?.id,
      });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.template) {
      const unsubscribe = iclApi.subscribeToTemplateICLs({ setNewICLs: setIcls });

      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
    if (tab === tabs.all) {
      const unsubscribe = iclApi.subscribeToAdminICLs({ setNewICLs: setIcls });
      setIsLoading(false);

      return () => {
        unsubscribe();
      };
    }
  }, [tab]);

  useEffect(() => {
    setIclsForShow(updateIclsForShow(icls, page));
  }, [icls, page]);

  const updateIclsForShow = (icls: ICL[], page: number) => {
    const start = (page - 1) * tableSize;
    const end = start + tableSize;
    return icls.slice(start, end);
  };

  const handleNavigateToChecklistUpdate = (id: string) => {
    navigate('/checklist-update', { state: { ICLId: id } });
  };

  const handleDeleteICL = async (id: string) => {
    setIsRemovingLoading(true);

    try {
      await iclApi.removeICL(id);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong. Try again!'));
    }

    toast.success(t('Successfully removed!'));
    setIsModalOpen('');
    setIsRemovingLoading(false);
  };

  const handleOpenDeletePopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.delete);
    setIdForManipulation(id);
  };

  const handleConsiderTemplate = async (id: string, accept: boolean) => {
    const targetIcl = icls.find(it => it.id === id);

    if (targetIcl) {
      if (Object.prototype.hasOwnProperty.call(targetIcl, 'isConsideration')) {
        targetIcl['isConsideration'] = false;

        if (accept) {
          await iclApi.copyICLToAdmin(targetIcl).then(() => {
            setTab(tabs.all);
            toast.success(t('Successfully added!'));
          });
        } else {
          toast.success(t('Successfully denied!'));
        }
      }

      await iclApi.updateICL(targetIcl);
    }
  };

  const handlePopupActions = () => {
    switch (isModalOpen) {
      case 'delete':
        handleDeleteICL(idForManipulation);
        break;
      case 'copyAsAdmin':
        handleConsiderTemplate(idForManipulation, true);
        break;
      case 'denied':
        handleConsiderTemplate(idForManipulation, false);
    }

    setIsModalOpen('');
  };

  const actionPopupButtonTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'delete';
      case checklistTablePopupsNames.copyAsAdmin:
        return 'copyAsTetherButton';
      case checklistTablePopupsNames.denied:
        return 'deniedButton';
      default:
        return '';
    }
  }, [isModalOpen]);

  const actionPopupTitle = useMemo(() => {
    switch (isModalOpen) {
      case checklistTablePopupsNames.delete:
        return 'want to delete icl';
      case checklistTablePopupsNames.copyAsAdmin:
        return 'copyAsAdmin';
      case checklistTablePopupsNames.denied:
        return 'denied';
      default:
        return '';
    }
  }, [isModalOpen]);

  const handleOpenCopyPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.copyAsAdmin);
    setIdForManipulation(id);
  };

  const handleOpenDeniedPopup = (id: string) => {
    setIsModalOpen(checklistTablePopupsNames.denied);
    setIdForManipulation(id);
  };

  const renderConsiderationButton = (id: string, isConsideration: boolean, isTemplate: boolean) => {
    if (!isTemplate && isConsideration && tab === tabs.consideration) {
      return (
        <div className={'flex items-center gap-2'}>
          <button className="btn btn-primary more" onClick={() => handleOpenCopyPopup(id)}>
            {t('Accept')}
          </button>
          <button className="btn more" onClick={() => handleOpenDeniedPopup(id)}>
            {t('Denied')}
          </button>
        </div>
      );
    }

    if (!isTemplate && !isConsideration) {
      return (
        <Tooltip
          title={t('copyAsTether')}
          componentsProps={{
            popper: {
              sx: {
                ['& .MuiTooltip-arrow']: {
                  color: '#9CDFFC',
                },
                ['& .MuiTooltip-tooltip']: {
                  backgroundColor: '#9CDFFC',
                  color: '#000000',
                  fontSize: '14px',
                  padding: '14px',
                  borderRadius: '8px',
                },
              },
            },
          }}
          arrow
        >
          <button
            className="btn more small h-[40px] w-[40px] rounded-[8px] !p-[6px]"
            onClick={() => handleOpenCopyPopup(id)}
          >
            <img src={copySvg} alt="copy" />
          </button>
        </Tooltip>
      );
    }
  };

  const handleChangePage = async (newPage: number) => {
    setPage(newPage);
  };

  const handleApplyFilters = () => {
    setClick(!click);
  };

  const handleResetFilters = () => {
    setClick(!click);
    setTeacherFilter(null);
    setStudentFilter(null);
    setSubjectFilter(null);
    setClassFilter(null);
  };

  const handleChangeTab = (tab: string) => {
    setTab(tab);
    setPage(1);
    handleResetFilters();
  };

  const getFIOFromEmail = (email: string) => {
    for (const student of students) {
      if (student.email?.replace(/\./g, '') === email) {
        return student.FIO;
      }
    }

    for (const teacher of teachers) {
      if (teacher.email?.replace(/\./g, '') === email) {
        return teacher.FIO;
      }
    }

    return email;
  };

  const renderRows = () => {
    return activeIcls.map(
      ({ id, theme, studentEmail, teacherEmail, subject, class: studentClass, isConsideration, isTemplate }) => {
        return (
          <TableRow key={id}>
            <TableCell
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                position: 'sticky',
                left: 0,
                backgroundColor: 'white',
                textOverflow: 'ellipsis',
              }}
            >
              {theme}
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>{t(studentClass)}</TableCell>
            <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
            {tab === tabs.all && <TableCell>{getFIOFromEmail(studentEmail)}</TableCell>}
            {tab === tabs.all && <TableCell>{getFIOFromEmail(teacherEmail)}</TableCell>}
            <TableCell>
              <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
                <button className="btn btn-primary more" onClick={() => handleNavigateToChecklistUpdate(id)}>
                  {t('editing')}
                </button>

                <button
                  className="btn more small h-[40px] w-[40px] rounded-[8px]"
                  onClick={() => handleOpenDeletePopup(id)}
                >
                  <span className="icon icon-delete text-red-600" />
                </button>

                {renderConsiderationButton(id, isConsideration ?? false, isTemplate)}

                {isTemplate && (
                  <Tooltip
                    title={t('deleteCheckList')}
                    componentsProps={{
                      popper: {
                        sx: {
                          ['& .MuiTooltip-arrow']: {
                            color: '#9CDFFC',
                          },
                          ['& .MuiTooltip-tooltip']: {
                            backgroundColor: '#9CDFFC',
                            color: '#000000',
                            fontSize: '14px',
                            padding: '14px',
                            borderRadius: '8px',
                          },
                        },
                      },
                    }}
                    arrow
                  ></Tooltip>
                )}
              </div>
            </TableCell>
          </TableRow>
        );
      },
    );
  };

  const renderDeletedRows = () => {
    return removedIcls.map(
      ({ id, theme, studentEmail, teacherEmail, subject, class: studentClass, isConsideration, isTemplate }) => {
        return (
          <TableRow key={id}>
            <TableCell
              sx={{
                maxWidth: 200,
                overflow: 'hidden',
                position: 'sticky',
                left: 0,
                backgroundColor: 'white',
                textOverflow: 'ellipsis',
              }}
            >
              {theme}
            </TableCell>
            <TableCell sx={{ minWidth: 150 }}>{t(studentClass)}</TableCell>
            <TableCell>{platformLang === 'ua' ? subject.name : subject.ruName}</TableCell>
            {tab === tabs.all && <TableCell>{getFIOFromEmail(studentEmail)}</TableCell>}
            {tab === tabs.all && <TableCell>{getFIOFromEmail(teacherEmail)}</TableCell>}
            <TableCell>
              <div className="flex flex-row justify-end gap-[10px] [&>*]:!text-[16px] lg:[&>*]:!text-[18px]">
                <button className="btn btn-primary more" onClick={() => restoreDeletedIcl(id)}>
                  {t('restore')}
                </button>
              </div>
            </TableCell>
          </TableRow>
        );
      },
    );
  };

  const fullListLength = useMemo(() => {
    return tab === tabs.deletedTemplates
      ? icls.filter(icl => icl.deleted).length
      : icls.filter(icl => !icl.deleted).length;
  }, [icls, tab]);

  const hasRows = (tab === tabs.deletedTemplates ? removedIcls : activeIcls).length > 0;

  const getEmptyMessage = () => {
    switch (tab) {
      case tabs.all:
        return t('noChecklists');

      case tabs.consideration:
        return t('noChecklistConsideration');

      case tabs.template:
        return t('noChecklistTemplates');

      case tabs.deletedTemplates:
        return t('noDeletedChecklistTemplates');

      default:
        return t('noChecklists');
    }
  };

  return (
    <LoadingError isLoading={isLoading && !icls}>
      <div>
        <div className="mb-[20px] flex gap-[8px] lg:block">
          <TableTabs setTab={handleChangeTab} tab={tab} configArr={adminTabs} />

          {students && teachers && subjects && (
            <CheckListFilters
              subject={subjectFilter}
              student={studentFilter}
              teacher={teacherFilter}
              studentClass={classFilter}
              setClassFilter={setClassFilter}
              setStudentFilter={setStudentFilter}
              setTeacherFilter={setTeacherFilter}
              setSubjectFilter={setSubjectFilter}
              teachers={teachers}
              students={students}
              handleApply={handleApplyFilters}
              handleReset={handleResetFilters}
              click={click}
              subjects={subjects}
              tab={tab}
            />
          )}
        </div>

        <div className="mb-[20px] rounded-[24px] bg-white [box-shadow:10px_10px_0px_0px_#EBEAE7] lg:px-[20px] lg:py-[10px]">
          <TableContainer sx={{ borderRadius: '24px' }}>
            <Table aria-label="simple table">
              {hasRows && (
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        maxWidth: 200,
                        position: 'sticky',
                        left: 0,
                        backgroundColor: 'white',
                        borderTopLeftRadius: '24px',
                      }}
                    >
                      {t('theme')}
                    </TableCell>
                    <TableCell sx={{ minWidth: 150 }}>{t('class')}</TableCell>
                    <TableCell>{t('subject')}</TableCell>
                    {tab === tabs.all && <TableCell>{t('alreadySend')}</TableCell>}
                    {tab === tabs.all && <TableCell>{t('Teacher')}</TableCell>}
                    <TableCell></TableCell>
                  </TableRow>
                </TableHead>
              )}
              <TableBody>
                {!hasRows && !isLoading ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center">
                      {getEmptyMessage()}
                    </TableCell>
                  </TableRow>
                ) : tab === tabs.deletedTemplates ? (
                  renderDeletedRows()
                ) : (
                  renderRows()
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        {hasRows && (
          <Stack alignItems="center">
            <Pagination
              sx={{
                '& .MuiPaginationItem-page.Mui-selected': {
                  backgroundColor: 'white',
                  border: '1px solid #52C7FA',
                },
                '& .MuiPaginationItem-page': {
                  color: '#656D7B',
                },
              }}
              shape={'rounded'}
              page={page}
              onChange={(_, page) => handleChangePage(page)}
              count={Math.ceil(fullListLength / tableSize)}
            />
          </Stack>
        )}

        <ItemActionModal
          isOpen={Boolean(isModalOpen)}
          close={() => setIsModalOpen('')}
          title={actionPopupTitle}
          onHandle={() => handlePopupActions()}
          isSubmitButtonDisabled={isRemovingLoading}
          buttonTitle={actionPopupButtonTitle}
        />
      </div>
    </LoadingError>
  );
}
